import React, { useRef, useCallback, useState, useEffect, useContext } from "react";
import { connect } from "react-redux";
import { getCurrentLesson, getCurrentLessonId } from "../../selectors/lessonSelectors";
import { getCurrentLessonQuestionAndStem, getFlattenedLessonQuestions } from "../../selectors/lessonQuestionSelectors";
import { getLessonQuestionAnswered } from "../../selectors/answerSelector";
import { getIsWatchedByLessonId } from "../../selectors/lessonsUserSelectors";
import { LessonTabControlContext } from "../../contexts/LessonTabControlContext";
import { fetchQuestionReplies } from "../../processes/replyProccess";
import { fetchLessonAnswers } from "../../processes/answerProccess";
import useScreenVerticalOffset from "../../utils/useScreenVerticalOffset";
import LessonQuestionAlert from "./LessonQuestionAlert";

function LessonQuestionAlertContainerInner(props) {
  const {
    watched,
    lesson,
    hasQuestions,
    dispatch,
    loadingAnswer,
    loadingAlternatives,
  } = props;
  const tabControlRef = useContext(LessonTabControlContext);
  const questionRef = useRef(null);
  const scrollAmount = useScreenVerticalOffset();
  const [wasAlerted, setWasAlerted] = useState(false);

  useEffect(() => {
    if (lesson?.id && hasQuestions && (lesson?.meta?.question || (lesson?.meta?.questions && lesson.meta.questions.length > 0))) {
      fetchLessonAnswers(dispatch, lesson?.courseId, lesson.id);
    }
  }, [dispatch, lesson, hasQuestions]);

  useEffect(() => {
    if (lesson?.id && hasQuestions) {
      const questionIds = [];

      if (lesson.meta?.question) {
        questionIds.push(lesson.meta.question);
      }

      if (lesson.meta?.questions && Array.isArray(lesson.meta.questions)) {
        lesson.meta.questions.forEach((questionId) => {
          if (questionId) {
            questionIds.push(questionId);
          }
        });
      }

      if (questionIds.length > 0) {
        questionIds.forEach(questionId => {
          fetchQuestionReplies(dispatch, questionId);
        });
      }
    }
  }, [dispatch, lesson?.id, lesson?.meta?.question, lesson?.meta?.questions, hasQuestions]);

  useEffect(() => {
    if (scrollAmount > 100 && !wasAlerted) {
      setWasAlerted(true);
    }
  }, [scrollAmount, wasAlerted]);

  const handleGoToQuestion = useCallback(() => {
    if (!tabControlRef?.current) {
      return;
    }

    const questionsTabIndex = tabControlRef.current.getQuestionsTabIndex();

    if (questionsTabIndex >= 0) {
      tabControlRef.current.setActiveTabIndex(questionsTabIndex);

      let attempts = 0;
      const maxAttempts = 20;

      const checkAndScroll = () => {
        attempts++;
        const questionElement = document.querySelector('[data-question-ref]');

        if (questionElement) {
          setTimeout(() => {
            questionElement.scrollIntoView({
              behavior: "smooth",
              block: "start",
              inline: "nearest",
            });
          }, 100);
        } else if (attempts < maxAttempts) {
          setTimeout(checkAndScroll, 100);
        }
      };

      setTimeout(checkAndScroll, 300);
    }
  }, [tabControlRef]);

  const questionReady = !loadingAnswer && !loadingAlternatives;

  const shouldShowAlert = watched && hasQuestions && questionReady && props.hasUnansweredQuestions && !wasAlerted && scrollAmount < 100;

  if (!shouldShowAlert) {
    return null;
  }

  return (
    <LessonQuestionAlert
      questionRef={questionRef}
      onGoToQuestion={handleGoToQuestion}
    />
  );
}

function mapStateToProps(state) {
  const currentLessonId = getCurrentLessonId(state);
  const lesson = getCurrentLesson(state, currentLessonId);
  const currentQuestionItem = getCurrentLessonQuestionAndStem(state);
  const flattenedLessonQuestions = getFlattenedLessonQuestions(state);
  const hasQuestions = flattenedLessonQuestions && flattenedLessonQuestions.length > 0;
  const watched = getIsWatchedByLessonId(state, lesson?.id);
  const loadingAlternatives = state.lesson.loadingAlternatives;
  const loadingAnswer = state.lesson.loadingAnswer;

  // Verifica se há questões não respondidas usando o selector reutilizável
  let hasUnansweredQuestions = false;
  if (flattenedLessonQuestions && flattenedLessonQuestions.length > 0) {
    hasUnansweredQuestions = flattenedLessonQuestions.some((questionItem) => {
      if (!questionItem.question) {
        return false;
      }

      const answered = getLessonQuestionAnswered(state, {
        questionItem,
        lessonId: lesson?.id,
      });

      return !answered;
    });
  }

  // Usa selector reutilizável para verificar se a questão foi respondida
  const answered = getLessonQuestionAnswered(state, {
    questionItem: currentQuestionItem,
    lessonId: lesson?.id,
  });

  return {
    lesson,
    currentQuestionItem,
    answered,
    hasQuestions,
    watched,
    hasUnansweredQuestions,
    loadingAnswer,
    loadingAlternatives,
  };
}

export default connect(mapStateToProps)(LessonQuestionAlertContainerInner);
